import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import type { FireLoopResponse, LoopDto, ProjectKind } from '@aiai/shared';
import { fireLoop, updateLoop } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectHref } from '../lib/projectNav';
import { useWorkspacePath } from './useWorkspacePath';
import { Callout } from '../components/Callout';
import { Flash } from '../components/Flash';
import { Spinner } from '../components/Spinner';

/**
 * Shared fire-now + enable/disable controls for a single loop, used by both the
 * project Loops page and the Overview "Automations" section so the inline
 * behavior (mutations, flash, invalidation) stays identical in both places.
 *
 * The flash / error rows are returned separately as `feedback` because they
 * render as sibling `<li>`s inside the surrounding `RowList`, a spot a single
 * inline component can't reach.
 */
export function useLoopControls(loop: LoopDto, projectKind: ProjectKind) {
  const { projectId, projectCode } = loop;
  const { wp } = useWorkspacePath();
  const [fireResult, setFireResult] = useState<string | null>(null);
  const [firedRun, setFiredRun] = useState<FireLoopResponse | null>(null);
  const [fireSkipped, setFireSkipped] = useState(false);
  const queryClient = useQueryClient();

  // Keep both the project-scoped loop list and the cross-project Automations
  // list fresh, regardless of which page mounted these controls.
  function invalidateLoops() {
    queryClient.invalidateQueries({ queryKey: qk.projectLoops(projectId) });
    queryClient.invalidateQueries({ queryKey: qk.loops });
  }

  const fireMutation = useMutation({
    mutationFn: () => fireLoop(loop.id),
    onSuccess: (result) => {
      if ('skipped' in result) {
        // A manual fire forces past the still-active/queued guard, so the only
        // skip left is an Engineer (drain) loop with nothing ready to build.
        setFireResult('Backlog is clear — nothing left to build right now.');
        setFiredRun(null);
        setFireSkipped(true);
      } else {
        setFireResult('Fired!');
        setFiredRun(result);
        setFireSkipped(false);
        invalidateLoops();
        queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
      }
    },
  });

  const enableMutation = useMutation({
    mutationFn: () => updateLoop(loop.id, { enabled: !loop.enabled }),
    onSuccess: invalidateLoops,
  });

  const fireButton = (
    <button
      type="button"
      aria-label="Fire now"
      title="Fire now"
      disabled={fireMutation.isPending || !loop.enabled}
      onClick={(e) => {
        e.preventDefault();
        fireMutation.mutate();
      }}
      className="inline-flex items-center justify-center rounded p-1.5 text-ink-muted hover:bg-surface-raised hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {fireMutation.isPending ? (
        <Spinner className="h-4 w-4" />
      ) : (
        <Play className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );

  const toggle = (
    <button
      type="button"
      role="switch"
      aria-checked={loop.enabled}
      aria-label={loop.enabled ? 'Disable loop' : 'Enable loop'}
      title={loop.enabled ? 'Disable loop' : 'Enable loop'}
      disabled={enableMutation.isPending}
      onClick={(e) => {
        e.preventDefault();
        enableMutation.mutate();
      }}
      className={[
        'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        loop.enabled ? 'bg-brand' : 'bg-surface-raised',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          loop.enabled ? 'translate-x-4' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );

  const feedback = (
    <>
      {fireResult && (
        <li>
          <div className="px-4 py-2">
            <Flash
              onDone={() => {
                setFireResult(null);
                setFiredRun(null);
                setFireSkipped(false);
              }}
            >
              {fireResult}
              {firedRun && (
                <>
                  {' · '}
                  <Link to={wp(`/runs/${firedRun.runId}`)} className="underline">
                    View run
                  </Link>
                </>
              )}
              {fireSkipped && (
                <>
                  {' · '}
                  <Link to={wp(projectHref(projectKind, projectCode ?? projectId))} className="underline">
                    View {projectKind === 'prototype' ? 'prototype' : 'project'}
                  </Link>
                </>
              )}
            </Flash>
          </div>
        </li>
      )}

      {fireMutation.isError && (
        <li>
          <div className="px-4 py-2">
            <Callout tone="danger">{errorMessage(fireMutation.error, 'Fire failed')}</Callout>
          </div>
        </li>
      )}

      {enableMutation.isError && (
        <li>
          <div className="px-4 py-2">
            <Callout tone="danger">
              {errorMessage(enableMutation.error, 'Could not update loop')}
            </Callout>
          </div>
        </li>
      )}
    </>
  );

  return {
    fireButton,
    toggle,
    feedback,
    /** Imperative fire handle for menu items that trigger the same action. */
    fire: () => fireMutation.mutate(),
    firePending: fireMutation.isPending,
  };
}
